import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, message, Spin, Space, Card, Drawer, List, Avatar } from "antd";
import { SaveOutlined, ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import DashboardGrid from "../components/DashboardGrid";
import { dashboardService } from "../../../api/enhancedApi";
import { WIDGET_REGISTRY } from "../registry/widgetRegistry";

const DashboardTemplateEditor = () => {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const [layout, setLayout] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [template, setTemplate] = useState(null);
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);

    const fetchTemplate = useCallback(async () => {
        setLoading(true);
        try {
            const data = await dashboardService.getTemplate(templateId);
            setTemplate(data);
            // 确保 layout_data 是数组
            setLayout(Array.isArray(data.layout_data) ? data.layout_data : []);
        } catch (error) {
            console.error("Failed to fetch template:", error);
            message.error("获取模版详情失败");
        } finally {
            setLoading(false);
        }
    }, [templateId]);

    useEffect(() => {
        if (templateId) {
            fetchTemplate();
        }
    }, [fetchTemplate, templateId]);

    const handleLayoutChange = (newLayout) => {
        // React-Grid-Layout 返回的 layout 对象包含了位置信息
        // 我们需要保留它，但也要确保它包含 i (key)
        // RGL 自动处理了 x, y, w, h 的更新
        setLayout(newLayout);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // 只保存 layout_data 字段
            await dashboardService.updateTemplate(templateId, { layout_data: layout });
            message.success("模版布局保存成功");
        } catch (error) {
            console.error("Failed to save template layout:", error);
            message.error("保存失败");
        } finally {
            setSaving(false);
        }
    };

    const handleAddWidget = (widgetKey) => {
        const widgetConfig = WIDGET_REGISTRY[widgetKey];
        if (!widgetConfig) return;

        // 计算新组件的位置 (简单的放在底部或第一个空位)
        // 这里简单实现：放在最下面
        const newY = layout.length > 0 ? Math.max(...layout.map((item) => item.y + item.h)) : 0;

        const newItem = {
            i: widgetKey,
            x: 0,
            y: newY,
            w: widgetConfig.defaultSize?.w || 4,
            h: widgetConfig.defaultSize?.h || 4,
        };

        setLayout([...layout, newItem]);
        message.success(`已添加组件: ${widgetConfig.title}`);
        setIsDrawerVisible(false);
    };

    const availableWidgets = Object.keys(WIDGET_REGISTRY).map((key) => ({
        key,
        ...WIDGET_REGISTRY[key],
    }));

    const handleRemoveItem = (itemId) => {
        const newLayout = layout.filter((item) => item.i !== itemId);
        setLayout(newLayout);
    };

    if (loading) {
        return (
            <div style={{ padding: 50, textAlign: "center" }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header-compact">
                <div className="page-title-compact">
                    <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate("/templates")} style={{ marginRight: 8 }} />
                    设计模版: {template?.name}
                </div>
                <div className="page-action-compact">
                    <Space>
                        <Button icon={<PlusOutlined />} onClick={() => setIsDrawerVisible(true)}>
                            添加组件
                        </Button>
                        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
                            保存布局
                        </Button>
                    </Space>
                </div>
            </div>

            <div style={{ padding: "0 24px 24px" }}>
                <DashboardGrid layout={layout} onLayoutChange={handleLayoutChange} isDraggable={true} isResizable={true} onRemoveItem={handleRemoveItem} />
            </div>

            <Drawer title="添加组件" placement="right" onClose={() => setIsDrawerVisible(false)} open={isDrawerVisible} width={400}>
                <List
                    itemLayout="horizontal"
                    dataSource={availableWidgets}
                    renderItem={(item) => (
                        <List.Item
                            actions={[
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => handleAddWidget(item.key)}
                                    disabled={layout.some((l) => l.i === item.key)} // 假设每个组件只能添加一次，如果允许重复则去掉此检查
                                >
                                    添加
                                </Button>,
                            ]}
                        >
                            <List.Item.Meta title={item.title} description={`默认尺寸: ${item.defaultSize?.w || 4}x${item.defaultSize?.h || 4}`} />
                        </List.Item>
                    )}
                />
            </Drawer>
        </div>
    );
};

export default DashboardTemplateEditor;
